import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';
import NECSLogo from './NECSLogo';
import SmsConsentCheckbox, { phoneRequiresConsent } from './SmsConsentCheckbox';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';
import '../styles/public-page-titles.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Cached promises — fetched once per page load regardless of StrictMode mount/unmount.
let _propertiesPromise = null;
let _locationsPromise = null;
function fetchPublicProperties() {
  if (!_propertiesPromise) {
    _propertiesPromise = (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/tenant/public-properties`);
        const data = await r.json();
        return data?.properties || [];
      } catch {
        return [];
      }
    })();
  }
  return _propertiesPromise;
}
function fetchPropertyLocations() {
  if (!_locationsPromise) {
    _locationsPromise = (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/tenant/property-locations`);
        const data = await r.json();
        return data || { countries: [], by_country: {} };
      } catch {
        return { countries: [], by_country: {} };
      }
    })();
  }
  return _locationsPromise;
}
if (typeof window !== 'undefined') {
  fetchPublicProperties();
  fetchPropertyLocations();
}

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC',
];

// USPS 2-letter → full state name — the countriesnow.space API expects the
// full name for its state/cities lookup.
const US_STATE_LABELS = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
  MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
  OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  DC: 'District of Columbia',
};

// Country → human label. Extend here as we launch more markets.
const COUNTRY_LABELS = { US: 'United States', CA: 'Canada', MX: 'Mexico' };

/**
 * Tenant / Resident registration form. Self-signup is gated:
 * the entered email must match an account-holder on the chosen property's contract.
 */
export default function TenantRegisterForm() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [properties, setProperties] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(true);
  const [locations, setLocations] = useState({ countries: [], by_country: {} });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  // A2P 10DLC — SMS opt-in gate; required whenever a phone number is entered.
  const [smsConsent, setSmsConsent] = useState(false);

  // Master Architecture Feb 2026 — Full tiered location filter for Resident
  // sign-up (restored iter-80 for Canadian expansion). Country → State →
  // City → Zip → Property. Each tier filters the next; residents type or
  // pick until only their community is visible.
  const [pickCountry, setPickCountry] = useState('US');
  const [pickState, setPickState] = useState('');
  const [pickCity, setPickCity] = useState('');
  const [pickZip, setPickZip] = useState('');

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    password: '',
    confirm_password: '',
    property_code: '',
    property_id: '',
    unit_number: '',
    city: '',
    state: 'TX',
  });

  // Iter-188 · Property/Facility ID (NECS-…) is the universal primary
  // reference across every transaction. The drilldown remains a
  // fallback when the applicant doesn't have their code in hand.
  const [codeLookup, setCodeLookup] = useState({ status: 'idle', message: '', match: null });

  // Fetch the property list + tiered location index once on mount.
  useEffect(() => {
    Promise.all([fetchPublicProperties(), fetchPropertyLocations()]).then(([rows, locs]) => {
      setProperties(rows);
      setLocations(locs || { countries: [], by_country: {} });
      setPropertiesLoading(false);
      // Preselect the first available country if US isn't seeded.
      if (locs?.countries?.length && !locs.countries.includes('US')) {
        setPickCountry(locs.countries[0]);
      }
    });
  }, []);

  // Full US state → city catalog fetched on-demand from a free public API
  // (countriesnow.space). We cache per-state so subsequent selections are
  // instant, and merge NECS-seeded cities in on top so contracted cities
  // always float to the top of the list.
  const [allCitiesByState, setAllCitiesByState] = useState({});
  const [citiesLoading, setCitiesLoading] = useState(false);

  useEffect(() => {
    // Skip if unknown country, already cached, or no state selected.
    if (pickCountry !== 'US' || !pickState || allCitiesByState[pickState]) return;
    let cancelled = false;
    setCitiesLoading(true);
    (async () => {
      try {
        const res = await fetch('https://countriesnow.space/api/v0.1/countries/state/cities', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ country: 'United States', state: US_STATE_LABELS[pickState] || pickState }),
        });
        const data = await res.json();
        if (cancelled) return;
        const list = Array.isArray(data?.data) ? data.data : [];
        setAllCitiesByState((c) => ({ ...c, [pickState]: list }));
      } catch (_) {
        // Network / API blip — the UI will fall back to seeded cities only.
      } finally {
        if (!cancelled) setCitiesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pickCountry, pickState, allCitiesByState]);

  // Full ZIP list per city fetched from Zippopotam.us. It returns places for
  // a US state; we filter down to the selected city. Cached per state.
  const [zipsByStateCity, setZipsByStateCity] = useState({});
  const [zipsLoading, setZipsLoading] = useState(false);

  useEffect(() => {
    if (pickCountry !== 'US' || !pickState || !pickCity) return;
    const cacheKey = `${pickState}::${pickCity.toLowerCase()}`;
    if (zipsByStateCity[cacheKey]) return;
    let cancelled = false;
    setZipsLoading(true);
    (async () => {
      try {
        // Zippopotam returns up to ~200 places per state — enough coverage
        // for our use-case. Prefer the city+state endpoint for better hits.
        const url = `https://api.zippopotam.us/us/${encodeURIComponent(pickState)}/${encodeURIComponent(pickCity)}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error('no zips');
        const data = await res.json();
        if (cancelled) return;
        const places = Array.isArray(data?.places) ? data.places : [];
        const list = Array.from(new Set(places.map((p) => p['post code']).filter(Boolean))).sort();
        setZipsByStateCity((z) => ({ ...z, [cacheKey]: list }));
      } catch (_) {
        // silent
      } finally {
        if (!cancelled) setZipsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pickCountry, pickState, pickCity, zipsByStateCity]);

  // Cascading tier options — each derives from the currently-selected parent.
  // Nationwide Scalability Masterplan (iter-84): full US-city catalog for the
  // selected state, with NECS-contracted cities pinned to the top so residents
  // still see their community first.
  const states = useMemo(() => {
    if (pickCountry === 'US') return US_STATES;
    const c = locations.by_country?.[pickCountry] || {};
    return Object.keys(c).sort();
  }, [locations, pickCountry]);

  const seededCitiesForState = useMemo(() => {
    const bucket = locations.by_country?.[pickCountry]?.[pickState] || {};
    return Object.keys(bucket).sort();
  }, [locations, pickCountry, pickState]);

  const cities = useMemo(() => {
    const seeded = seededCitiesForState;
    const seededSet = new Set(seeded.map((c) => c.toLowerCase()));
    const full = allCitiesByState[pickState] || [];
    // Contracted cities first, then the rest of the state alphabetically.
    return [...seeded, ...full.filter((c) => !seededSet.has(c.toLowerCase())).sort()];
  }, [seededCitiesForState, allCitiesByState, pickState]);

  const seededZipsForCity = useMemo(() => {
    const c = locations.by_country?.[pickCountry]?.[pickState]?.[pickCity] || {};
    return Object.keys(c).sort();
  }, [locations, pickCountry, pickState, pickCity]);

  const zips = useMemo(() => {
    const seeded = seededZipsForCity;
    const seededSet = new Set(seeded);
    const key = `${pickState}::${(pickCity || '').toLowerCase()}`;
    const full = zipsByStateCity[key] || [];
    return [...seeded, ...full.filter((z) => !seededSet.has(z))];
  }, [seededZipsForCity, zipsByStateCity, pickState, pickCity]);

  const zipProperties = useMemo(() => {
    if (!pickZip) return [];
    return locations.by_country?.[pickCountry]?.[pickState]?.[pickCity]?.[pickZip] || [];
  }, [locations, pickCountry, pickState, pickCity, pickZip]);

  // Auto-populate the "City" / "State" fields below (next to Unit Number)
  // whenever the tiered picker selection above changes. Residents no longer
  // have to retype what they just picked.
  useEffect(() => {
    setForm((s) => ({
      ...s,
      state: pickState || s.state,
      city: pickCity || s.city,
    }));
  }, [pickState, pickCity]);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  // Iter-188 · debounced NECS ID lookup — validates the applicant's
  // Property/Facility ID and locks in the matched property so they
  // never register into the wrong building. Clears the drilldown
  // selection on a valid match so the two paths never fight each other.
  useEffect(() => {
    const raw = (form.property_code || '').trim().toUpperCase();
    if (!raw) {
      setCodeLookup({ status: 'idle', message: '', match: null });
      return;
    }
    if (!raw.startsWith('NECS-')) {
      setCodeLookup({ status: 'invalid', message: 'IDs start with NECS- (e.g., NECS-TX-R01-0042).', match: null });
      return;
    }
    setCodeLookup((s) => ({ ...s, status: 'checking', message: 'Checking…' }));
    const controller = new AbortController();
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/properties/lookup/by-code/${encodeURIComponent(raw)}`, { signal: controller.signal });
        const data = await res.json();
        if (!res.ok) {
          setCodeLookup({ status: 'invalid', message: data?.detail || 'Not on file.', match: null });
          return;
        }
        setCodeLookup({ status: 'valid', message: '', match: data });
        setForm((s) => ({
          ...s,
          property_id: data.property_id,
          city: data.city || s.city,
          state: data.state && data.state.length === 2 ? data.state : (s.state || 'TX'),
        }));
      } catch (err) {
        if (err.name !== 'AbortError') {
          setCodeLookup({ status: 'invalid', message: 'Could not validate — try again.', match: null });
        }
      }
    }, 400);
    return () => { controller.abort(); clearTimeout(t); };
  }, [form.property_code]);

  // When property changes, copy city/state/zip from the tiered selection
  // (falls back to the raw property doc for backwards-compat).
  const onPropertyChange = (e) => {
    const id = e.target.value;
    const p = properties.find((x) => x.property_id === id);
    setForm((s) => ({
      ...s,
      property_id: id,
      city: pickCity || p?.city || s.city,
      state: pickState || p?.state || s.state,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.property_code && !form.property_id) {
      toast.error('Enter your Property/Facility ID (NECS-…) or use the drilldown below to pick your property.');
      return;
    }
    if (form.property_code && codeLookup.status !== 'valid') {
      toast.error('Please enter a valid Property/Facility ID before continuing.');
      return;
    }
    if (form.password !== form.confirm_password) {
      toast.error('Passwords do not match. Please confirm your password.');
      return;
    }
    if (phoneRequiresConsent(form.phone, smsConsent)) {
      toast.error('Please agree to receive SMS messages before continuing, or clear the phone number.');
      return;
    }
    setLoading(true);
    try {
      const { confirm_password, ...payload } = form;
      // Normalize the NECS ID (uppercase, no spaces) before send.
      if (payload.property_code) {
        payload.property_code = payload.property_code.trim().toUpperCase();
      }
      const res = await fetch(`${BACKEND_URL}/api/tenant-register`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Registration failed.');
      toast.success(`Welcome, ${data.user.first_name}!`);
      setUser(data.user);
      // role-based redirect already handled by Router, but force navigate just in case
      setTimeout(() => navigate('/tenant-dashboard'), 800);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-2xl mx-auto p-4 sm:p-6 md:p-8" data-testid="tenant-register-form">
      <div className="flex justify-center mb-4">
        <NECSLogo variant="header" size="sm" dataTestId="tenant-register-logo" />
      </div>

      <h2
        className="necs-web-page-title text-center mb-2"
        data-testid="tenant-register-title"
      >
        Tenant / Resident Registration
      </h2>
      <p className="text-center text-sm text-zinc-600 mb-6">
        Only the email listed as an <strong>account holder on your property&apos;s contract</strong> can register here.
        After signing in, you&apos;ll be able to authorize up to 3 additional users from your dashboard.
      </p>

      <form onSubmit={submit} className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="t_first">First Name *</Label>
            <Input id="t_first" required value={form.first_name} onChange={set('first_name')} data-testid="t-first-name" />
          </div>
          <div>
            <Label htmlFor="t_last">Last Name *</Label>
            <Input id="t_last" required value={form.last_name} onChange={set('last_name')} data-testid="t-last-name" />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="t_email">Email *</Label>
            <Input id="t_email" type="email" required value={form.email} onChange={set('email')} data-testid="t-email" />
            <p className="text-[11px] text-zinc-500 mt-1">
              Must match the account-holder email on your property&apos;s contract.
            </p>
          </div>
          <div>
            <Label htmlFor="t_phone">Phone Number *</Label>
            <Input id="t_phone" type="tel" required value={form.phone} onChange={set('phone')} placeholder="+1 214 555 0123" data-testid="t-phone" />
          </div>
        </div>

        <div>
          <Label htmlFor="t_password">Password *</Label>
          <div className="relative">
            <Input
              id="t_password"
              type={showPassword ? 'text' : 'password'}
              required
              minLength={6}
              value={form.password}
              onChange={set('password')}
              className="pr-10"
              data-testid="t-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute inset-y-0 right-0 flex items-center justify-center w-10 text-zinc-500 hover:text-[#EAB308]"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              data-testid="t-password-toggle"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div>
          <Label htmlFor="t_confirm_password">Confirm Password *</Label>
          <Input
            id="t_confirm_password"
            type={showPassword ? 'text' : 'password'}
            required
            minLength={6}
            value={form.confirm_password}
            onChange={set('confirm_password')}
            data-testid="t-confirm-password"
          />
          {form.confirm_password && form.password !== form.confirm_password && (
            <p className="text-xs text-red-500 mt-1" data-testid="t-confirm-password-mismatch">
              Passwords do not match.
            </p>
          )}
        </div>

        <div className="space-y-3" data-testid="tiered-property-filter">
          {/* Iter-188 · Primary path — Property/Facility ID (NECS-…) */}
          <div className="rounded-lg border-2 border-[#FFD60A]/50 bg-[#FFFBEA] p-4 space-y-2" data-testid="t-property-code-block">
            <Label htmlFor="t_property_code" className="font-bold text-[#2D2D2D]">
              Property / Facility ID <span className="text-red-500">*</span>
              <span className="ml-2 text-[10px] uppercase tracking-widest text-[#7a6300]">Preferred</span>
            </Label>
            <Input
              id="t_property_code"
              placeholder="NECS-TX-R01-0042"
              value={form.property_code}
              onChange={(e) => setForm((s) => ({ ...s, property_code: e.target.value.toUpperCase() }))}
              autoComplete="off"
              spellCheck={false}
              className="font-mono tracking-wide uppercase"
              data-testid="t-property-code"
            />
            {codeLookup.status === 'checking' && (
              <p className="text-[11px] text-zinc-500" data-testid="t-property-code-checking">Checking…</p>
            )}
            {codeLookup.status === 'valid' && codeLookup.match && (
              <div className="text-[12px] leading-snug rounded-md bg-emerald-50 border border-emerald-300 px-3 py-2 text-emerald-800" data-testid="t-property-code-match">
                <div className="font-semibold">✓ {codeLookup.match.property_name}</div>
                <div className="opacity-80">
                  {codeLookup.match.city}
                  {codeLookup.match.state ? `, ${codeLookup.match.state}` : ''}
                  {codeLookup.match.zip_code ? ` ${codeLookup.match.zip_code}` : ''}
                  {' · '}
                  <span className="uppercase tracking-widest text-[10px]">{codeLookup.match.property_type}</span>
                </div>
              </div>
            )}
            {codeLookup.status === 'invalid' && (
              <p className="text-[12px] text-red-600" data-testid="t-property-code-invalid">
                {codeLookup.message}
              </p>
            )}
            <p className="text-[11px] text-zinc-600">
              Multifamily residents and commercial tenants both use this ID. You&rsquo;ll find it on the welcome email your Property Manager or NECS Customer Service sent when your lease or contract was signed.
            </p>
          </div>

          <div className="text-center text-[11px] uppercase tracking-widest text-zinc-500 pt-1">
            — or search manually below —
          </div>

          <Label>Select Your Property *</Label>
          <p className="text-xs text-zinc-500 mt-0.5">
            Drill down to your home in seconds &mdash; start with your country. Canadian residents can now find their community here too.
          </p>
          <div className="grid md:grid-cols-4 gap-3">
            <div>
              <Label htmlFor="t_country" className="text-[11px] uppercase tracking-wider text-zinc-500">Country</Label>
              <select
                id="t_country"
                value={pickCountry}
                onChange={(e) => { setPickCountry(e.target.value); setPickState(''); setPickCity(''); setPickZip(''); setForm((s) => ({ ...s, property_id: '' })); }}
                className="w-full h-10 px-3 rounded-md border border-zinc-300 bg-white text-zinc-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD60A]"
                data-testid="t-tier-country"
              >
                {Array.from(new Set([...(locations.countries || []), 'US', 'CA'])).map((c) => (
                  <option key={c} value={c}>{COUNTRY_LABELS[c] || c}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="t_tier_state" className="text-[11px] uppercase tracking-wider text-zinc-500">State / Province</Label>
              <select
                id="t_tier_state"
                value={pickState}
                onChange={(e) => { setPickState(e.target.value); setPickCity(''); setPickZip(''); setForm((s) => ({ ...s, property_id: '' })); }}
                className="w-full h-10 px-3 rounded-md border border-zinc-300 bg-white text-zinc-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD60A] disabled:opacity-60"
                disabled={!states.length}
                data-testid="t-tier-state"
              >
                <option value="" disabled>{states.length ? 'Choose a state / province…' : 'No states seeded — try USA'}</option>
                {states.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label htmlFor="t_tier_city" className="text-[11px] uppercase tracking-wider text-zinc-500">City</Label>
              <select
                id="t_tier_city"
                value={pickCity}
                onChange={(e) => { setPickCity(e.target.value); setPickZip(''); setForm((s) => ({ ...s, property_id: '' })); }}
                className="w-full h-10 px-3 rounded-md border border-zinc-300 bg-white text-zinc-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD60A] disabled:opacity-60"
                disabled={citiesLoading || !cities.length}
                data-testid="t-tier-city"
              >
                <option value="" disabled>
                  {citiesLoading
                    ? 'Loading cities…'
                    : cities.length
                      ? 'Choose a city…'
                      : (pickState ? `Type your city — no NECS cities in ${pickState} yet` : 'Pick a state first')}
                </option>
                {cities.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label htmlFor="t_tier_zip" className="text-[11px] uppercase tracking-wider text-zinc-500">Zip / Postal Code</Label>
              <select
                id="t_tier_zip"
                value={pickZip}
                onChange={(e) => { setPickZip(e.target.value); setForm((s) => ({ ...s, property_id: '' })); }}
                className="w-full h-10 px-3 rounded-md border border-zinc-300 bg-white text-zinc-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD60A] disabled:opacity-60"
                disabled={zipsLoading || !zips.length}
                data-testid="t-tier-zip"
              >
                <option value="" disabled>
                  {zipsLoading
                    ? 'Loading zips…'
                    : zips.length
                      ? 'Choose a zip / postal…'
                      : (pickCity ? `No zips found for ${pickCity}` : 'Pick a city first')}
                </option>
                {zips.map((z) => <option key={z} value={z}>{z}</option>)}
              </select>
            </div>
          </div>

          <div>
            <Label htmlFor="t_property" className="text-[11px] uppercase tracking-wider text-zinc-500">Property</Label>
            <select
              id="t_property"
              required
              value={form.property_id}
              onChange={onPropertyChange}
              className="w-full h-10 px-3 rounded-md border border-zinc-300 bg-white text-zinc-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD60A] disabled:opacity-60"
              disabled={!pickZip || zipProperties.length === 0}
              data-testid="t-property"
            >
              <option value="">
                {propertiesLoading
                  ? 'Loading properties…'
                  : !pickZip
                    ? 'Finish the drilldown above to see your options'
                    : zipProperties.length === 0
                      ? 'No properties on file for this zip code yet'
                      : 'Choose your property'}
              </option>
              {zipProperties.map((p) => (
                <option key={p.property_id} value={p.property_id}>
                  {p.property_name}
                </option>
              ))}
            </select>
            {pickZip && zipProperties.length > 0 && (
              <p className="text-[11px] text-emerald-600 mt-1" data-testid="t-property-count">
                {zipProperties.length} propert{zipProperties.length === 1 ? 'y' : 'ies'} in {pickZip}
              </p>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="t_unit">Unit Number *</Label>
            <Input id="t_unit" required value={form.unit_number} onChange={set('unit_number')} placeholder="e.g., 312" data-testid="t-unit" />
          </div>
          <div>
            <Label htmlFor="t_city">City *</Label>
            <Input id="t_city" required value={form.city} onChange={set('city')} data-testid="t-city" />
          </div>
          <div>
            <Label htmlFor="t_state">State *</Label>
            <select
              id="t_state"
              required
              value={form.state}
              onChange={set('state')}
              className="w-full h-10 px-3 rounded-md border border-zinc-300 bg-white text-zinc-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD60A]"
              data-testid="t-state"
            >
              {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <SmsConsentCheckbox
          checked={smsConsent}
          onChange={setSmsConsent}
          testid="t-sms-consent"
        />

        <Button
          type="submit"
          className="w-full bg-[#FFD60A] text-black hover:bg-[#EAB308] font-bold"
          disabled={loading || phoneRequiresConsent(form.phone, smsConsent)}
          data-testid="t-submit"
        >
          {loading ? 'Creating account…' : 'Create my account'}
        </Button>

        <p className="text-center text-xs text-zinc-500 mt-3" data-testid="t-legal-links">
          By creating an account, you agree to our{' '}
          <Link to="/terms" className="text-[#FFD60A] hover:underline" data-testid="t-terms-link">Terms of Service</Link>
          {' '}and{' '}
          <Link to="/privacy" className="text-[#FFD60A] hover:underline" data-testid="t-privacy-link">Privacy Policy</Link>.
        </p>
      </form>

      <p className="text-center text-sm text-zinc-600 mt-6">
        Already have an account?{' '}
        <Link to="/login" className="text-[#FFD60A] hover:underline font-semibold" data-testid="t-login-link">
          Login here
        </Link>
      </p>

      <p className="text-center text-sm text-zinc-600 mt-3">
        <Link to="/login" className="text-[#FFD60A] hover:underline font-semibold" data-testid="t-back-prev-link">
          ← Back to Previous Page
        </Link>
      </p>
    </Card>
  );
}
